import { apiClient } from './apiClient';
import { httpErrorDetailMessage } from '../utils/httpErrorDetail';
import {
  formatPersistModeNotPatchMessage,
  normalizeControlBridgePersistMode,
  normalizeControlBridgeSotPlane,
  type ControlBridgePersistMode,
} from './controlBridgePersistMode';

export type ExoPanelExecutionAction = 'complete' | 'defer' | 'block' | 'start' | 'validate';

/** Persist plane reported by backend after Phase 4 patch routing. */
export type ExoPanelPersistMode = ControlBridgePersistMode;

export interface ExoPanelExecutionRequest {
  itemId: string;
  action: ExoPanelExecutionAction;
  /** Stable across transport retries of the same user action. */
  operationId?: string;
  /** Manifest activityTargetState — used to pick the matching transition on complete. */
  targetState?: string;
  blockReason?: string;
  /** Optional operator defer text; server falls back to ``Deferred from ExoPanel``. */
  deferReason?: string;
  /** Target item for derived-schema answers (tracked artifact or improvement item). */
  trackedItemId?: string;
  /** Property patches applied to trackedItemId before activity Complete. */
  propertyPatches?: Array<{ key: string; value: string; global_property_ref?: string }>;
}

export interface ExoPanelWriteBaseToken {
  artifactItemCount?: number;
  itemIdSetHash?: string;
  mtimeNs?: number;
}

export interface ExoPanelPatchReceipt {
  patchId?: string;
  itemsTouched?: string[];
  autoFireNext?: Record<string, unknown>;
  autoFireNextError?: string;
}

export interface ExoPanelExecutionResponse {
  ok: boolean;
  itemId: string;
  action: ExoPanelExecutionAction;
  processRef?: string;
  fromState?: string;
  toState?: string;
  workspaceSlug?: string;
  /**
   * How the transition was persisted.
   * F2 / Phase 4: `executeExoPanelAction` requires `patch` (Postgres) on hosted-SoT.
   * F5: disk-SoT models refuse hosted writes (Backend returns workspace_disk).
   */
  persistMode?: ExoPanelPersistMode;
  /** F5 SoT marker plane when Backend emits it. */
  sotPlane?: 'disk' | 'hosted';
  /** Optional write_base for subsequent shallow writes / conflict refresh. */
  writeBase?: ExoPanelWriteBaseToken;
  source?: 'db' | 'workspace';
  /** Postgres patch and optional named auto-hop outcome. */
  patchReceipt?: ExoPanelPatchReceipt;
}

export class ControlBridgeExoPanelExecutionError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly httpStatus?: number
  ) {
    super(message);
    this.name = 'ControlBridgeExoPanelExecutionError';
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function normalizeWriteBase(raw: unknown): ExoPanelWriteBaseToken | undefined {
  const obj = asRecord(raw);
  if (!obj) return undefined;
  const count = obj.artifactItemCount ?? obj.artifact_item_count;
  const hash = obj.itemIdSetHash ?? obj.item_id_set_hash;
  const mtime = obj.mtimeNs ?? obj.mtime_ns;
  const out: ExoPanelWriteBaseToken = {};
  if (typeof count === 'number') out.artifactItemCount = count;
  if (typeof hash === 'string') out.itemIdSetHash = hash;
  if (typeof mtime === 'number') out.mtimeNs = mtime;
  return Object.keys(out).length ? out : undefined;
}

function normalizePatchReceipt(raw: unknown): ExoPanelPatchReceipt | undefined {
  const obj = asRecord(raw);
  if (!obj) return undefined;
  const patchId = obj.patchId ?? obj.patch_id;
  const itemsTouched = obj.itemsTouched ?? obj.items_touched;
  const autoFireNext = obj.autoFireNext ?? obj.auto_fire_next;
  const autoFireNextError = obj.autoFireNextError ?? obj.auto_fire_next_error;
  const out: ExoPanelPatchReceipt = {};
  if (typeof patchId === 'string') out.patchId = patchId;
  if (Array.isArray(itemsTouched)) {
    out.itemsTouched = itemsTouched.filter((item): item is string => typeof item === 'string');
  }
  if (asRecord(autoFireNext)) out.autoFireNext = asRecord(autoFireNext) ?? undefined;
  if (typeof autoFireNextError === 'string') out.autoFireNextError = autoFireNextError;
  return Object.keys(out).length ? out : undefined;
}

const pendingOperationIds = new Map<string, string>();

function generatedOperationId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `exo-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

/** Normalize backend camel/snake execution payload for Control Bridge refresh. */
export function normalizeExoPanelExecutionResponse(
  data: unknown,
  fallback: { itemId: string; action: ExoPanelExecutionAction }
): ExoPanelExecutionResponse {
  const row = asRecord(data) ?? {};
  const persistMode = normalizeControlBridgePersistMode(row.persistMode ?? row.persist_mode);
  const sotPlane = normalizeControlBridgeSotPlane(row.sotPlane ?? row.sot_plane);
  const writeBase = normalizeWriteBase(row.writeBase ?? row.write_base ?? row.write_base_model);
  const patchReceipt = normalizePatchReceipt(row.patchReceipt ?? row.patch_receipt);
  const sourceRaw = row.source;
  const source =
    sourceRaw === 'db' || sourceRaw === 'workspace' ? sourceRaw : undefined;

  return {
    ok: row.ok !== false,
    itemId: (typeof row.itemId === 'string' && row.itemId) ||
      (typeof row.item_id === 'string' && row.item_id) ||
      fallback.itemId,
    action:
      (row.action === 'complete' ||
      row.action === 'defer' ||
      row.action === 'block' ||
      row.action === 'start' ||
      row.action === 'validate'
        ? row.action
        : fallback.action),
    processRef:
      (typeof row.processRef === 'string' && row.processRef) ||
      (typeof row.process_ref === 'string' ? row.process_ref : undefined),
    fromState:
      (typeof row.fromState === 'string' && row.fromState) ||
      (typeof row.from_state === 'string' ? row.from_state : undefined),
    toState:
      (typeof row.toState === 'string' && row.toState) ||
      (typeof row.to_state === 'string' ? row.to_state : undefined),
    workspaceSlug:
      (typeof row.workspaceSlug === 'string' && row.workspaceSlug) ||
      (typeof row.workspace_slug === 'string' ? row.workspace_slug : undefined),
    persistMode,
    sotPlane,
    writeBase,
    source,
    patchReceipt,
  };
}

/**
 * F2 / U2 / F5: Complete/Defer/Block/Start/Validate must land as Postgres patch
 * on hosted-SoT. Fail closed when the backend reports `workspace_disk` (disk-SoT
 * refuse) or omits `persistMode`.
 */
export function assertExoPanelPersistModePatch(
  response: ExoPanelExecutionResponse
): void {
  if (response.persistMode === 'patch') return;
  throw new ControlBridgeExoPanelExecutionError(
    formatPersistModeNotPatchMessage(
      'ExoPanel action',
      response.persistMode,
      response.sotPlane
    ),
    'PERSIST_MODE_NOT_PATCH'
  );
}

export async function executeExoPanelAction(
  workspaceSlug: string,
  request: ExoPanelExecutionRequest
): Promise<ExoPanelExecutionResponse> {
  const slug = workspaceSlug.trim();
  const itemId = request.itemId.trim();
  if (!slug || !itemId) {
    throw new ControlBridgeExoPanelExecutionError(
      'Workspace slug and item id are required',
      'NOT_CONFIGURED'
    );
  }

  const operationKey = JSON.stringify([
    slug,
    itemId,
    request.action,
    request.targetState,
    request.blockReason,
    request.deferReason,
    request.trackedItemId,
    request.propertyPatches,
  ]);
  const generatedOperation = !request.operationId;
  const operationId =
    request.operationId ??
    pendingOperationIds.get(operationKey) ??
    generatedOperationId();
  if (generatedOperation) pendingOperationIds.set(operationKey, operationId);

  try {
    const { data } = await apiClient.post<unknown>(
      `/kryspace/workspace/${encodeURIComponent(slug)}/exopanel-execution`,
      {
        itemId,
        action: request.action,
        targetState: request.targetState,
        blockReason: request.blockReason,
        deferReason: request.deferReason,
        operationId,
        trackedItemId: request.trackedItemId,
        propertyPatches: request.propertyPatches,
      }
    );
    const normalized = normalizeExoPanelExecutionResponse(data, {
      itemId,
      action: request.action,
    });
    // Assert before clearing operationId so a non-patch response keeps the same
    // id for a retry (server may have already applied a disk write).
    assertExoPanelPersistModePatch(normalized);
    if (generatedOperation) pendingOperationIds.delete(operationKey);
    return normalized;
  } catch (err: unknown) {
    if (err instanceof ControlBridgeExoPanelExecutionError) {
      // Keep operationId for PERSIST_MODE_NOT_PATCH so retries replay the same id.
      // Clear for NOT_CONFIGURED (thrown outside try) and for other client errors.
      if (err.code !== 'PERSIST_MODE_NOT_PATCH' && generatedOperation) {
        pendingOperationIds.delete(operationKey);
      }
      throw err;
    }
    const axiosErr = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
    // A received HTTP response is definitive. Network-ambiguous failures retain
    // the operation ID so the next identical action replays instead of reapplying.
    const responseStatus = axiosErr.response?.status;
    if (
      generatedOperation &&
      responseStatus !== undefined &&
      responseStatus >= 400 &&
      responseStatus < 500
    ) {
      pendingOperationIds.delete(operationKey);
    }
    const detail = axiosErr.response?.data?.detail;
    const message = httpErrorDetailMessage(
      detail,
      axiosErr.message || 'Failed to execute ExoPanel action'
    );
    throw new ControlBridgeExoPanelExecutionError(
      axiosErr.response?.status === 404 && !detail
        ? 'ExoPanel write API is not available on this server (restart backend after deploy)'
        : message,
      axiosErr.response?.status === 404 && !detail ? 'ROUTE_NOT_FOUND' : 'REQUEST_FAILED',
      axiosErr.response?.status
    );
  }
}
